import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useModal } from '../../../contexts/ModalContext';
import { updateItem } from '../../../api/items';
import { useBetaData } from '../../../contexts/BetaDataContext';
import MarkdownEditor from '../../common/MarkdownEditor';
import MarkdownViewer from '../../common/MarkdownViewer';
import Avatar from '../../common/Avatar';
import './BetaItemEditModal.scss';

const BetaItemEditModal = ({ isOpen, onClose, item, onSuccess }) => {
  const { profile } = useAuth();
  const { registerModal, unregisterModal } = useModal();
  const { usersMapByEmail } = useBetaData();
  const modalId = 'beta-item-edit-modal';

  const [loading, setLoading] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    completed: false,
  });

  useEffect(() => {
    if (isOpen) {
      registerModal(modalId);
    } else {
      unregisterModal(modalId);
    }
    return () => unregisterModal(modalId);
  }, [isOpen, registerModal, unregisterModal]);

  // 載入 item 資料到表單
  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || '',
        description: item.description || '',
        start_date: item.start_date || '',
        end_date: item.end_date || '',
        completed: item.status === 'completed',
      });
      setDescDraft(item.description || '');
      setEditingDesc(false);
    }
  }, [item]);

  const isOwner = item?.creator_id === profile?.id;

  if (!isOpen || !item) return null;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.title.trim()) return;
    setLoading(true);
    try {
      const updates = {
        title: formData.title.trim(),
        description: formData.description || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        status: formData.completed ? 'completed' : 'pending',
      };

      const { data, error } = await updateItem(item.id, updates);
      if (error) {
        console.error('Failed to update item:', error);
        return;
      }
      onSuccess?.(data);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleDescSave = () => {
    handleChange('description', descDraft);
    setEditingDesc(false);
  };

  // 解析建立者名稱
  const creatorEmail = item?.creator_profiles?.email?.toLowerCase();
  const creatorUser = creatorEmail ? usersMapByEmail[creatorEmail] : null;
  const creatorName = creatorUser?.name || item?.creator_profiles?.email || '未知';

  return (
    <div className="modal-overlay beta-item-modal-overlay">
      <div className="beta-item-modal">
        {/* Header */}
        <div className="bim-header">
          <h2>{isOwner ? '編輯任務' : '查看任務'}</h2>
          <button className="bim-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="bim-body">
          {/* Title */}
          <div className="bim-field">
            <label className="bim-label">標題</label>
            <input
              type="text"
              className="bim-input"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="任務標題"
              readOnly={!isOwner}
            />
          </div>

          {/* Completed toggle + Assignee row */}
          <div className="bim-row">
            <div className="bim-field">
              <label className="bim-label">完成狀態</label>
              <button
                type="button"
                className={`bim-toggle ${formData.completed ? 'completed' : ''}`}
                onClick={() => isOwner && handleChange('completed', !formData.completed)}
                disabled={!isOwner}
              >
                <span className="bim-toggle-track">
                  <span className="bim-toggle-thumb" />
                </span>
                <span className="bim-toggle-text">
                  {formData.completed ? '已完成' : '未完成'}
                </span>
              </button>
            </div>
            <div className="bim-field">
              <label className="bim-label">負責人</label>
              <div className="bim-assignee-readonly">
                <Avatar avatarUrl={creatorUser?.avatar_url} name={creatorName} size="xsmall" />
                <span>{creatorName}</span>
              </div>
            </div>
          </div>

          {/* Date row */}
          <div className="bim-row">
            <div className="bim-field">
              <label className="bim-label">開始日期</label>
              <input
                type="date"
                className="bim-input"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                readOnly={!isOwner}
              />
            </div>
            <div className="bim-field">
              <label className="bim-label">結束日期</label>
              <input
                type="date"
                className="bim-input"
                value={formData.end_date}
                onChange={(e) => handleChange('end_date', e.target.value)}
                readOnly={!isOwner}
              />
            </div>
          </div>

          {/* Description */}
          <div className="bim-field bim-desc-field">
            <div className="bim-desc-header">
              <label className="bim-label">說明</label>
              {!editingDesc && isOwner && (
                <button className="bim-desc-edit-btn" onClick={() => setEditingDesc(true)}>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M10 1.5l2.5 2.5M1 13l.8-3.2L10.3 1.3a1 1 0 011.4 0l1 1a1 1 0 010 1.4L4.2 12.2 1 13z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
            {editingDesc ? (
              <div className="bim-desc-editor">
                <MarkdownEditor
                  value={descDraft}
                  onChange={setDescDraft}
                  height="250px"
                  placeholder="輸入說明（支援 Markdown）..."
                />
                <div className="bim-desc-actions">
                  <button className="bim-btn-save" onClick={handleDescSave}>確認</button>
                  <button className="bim-btn-cancel" onClick={() => setEditingDesc(false)}>取消</button>
                </div>
              </div>
            ) : (
              <div className="bim-desc-content" onClick={() => isOwner && setEditingDesc(true)}>
                {formData.description ? (
                  <MarkdownViewer content={formData.description} />
                ) : (
                  <span className="bim-no-desc">{isOwner ? '點擊新增說明...' : '無說明'}</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bim-footer">
          <button className="bim-btn-cancel" onClick={onClose} disabled={loading}>
            {isOwner ? '取消' : '關閉'}
          </button>
          {isOwner && (
            <button className="bim-btn-primary" onClick={handleSave} disabled={loading || !formData.title.trim()}>
              {loading ? '儲存中...' : '儲存'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BetaItemEditModal;
